/**
 * Device Tokens Component
 * Display registered device push tokens
 * Matches the old admin page Tokens tab
 */

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { usePushTokens } from '../hooks/usePushNotifications';
import { useI18n } from '@/i18n';
import type { PushToken } from '@/types/push';

export function DeviceTokens() {
  const { messages } = useI18n();
  const t = messages.pushNotifications;
  const tc = messages.common;
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const limit = 50;

  const { data, isLoading, error, refetch } = usePushTokens({
    platform: platformFilter || undefined,
    active_only: true,
    page,
    limit,
  });

  const tokens = data?.tokens || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t.time.justNow;
    if (diffMins < 60) return t.time.minutesAgo.replace('{n}', String(diffMins));
    if (diffHours < 24) return t.time.hoursAgo.replace('{n}', String(diffHours));
    if (diffDays < 7) return t.time.daysAgo.replace('{n}', String(diffDays));

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getPlatformIcon = (platform: PushToken['platform']) => {
    return platform === 'ios' ? '🍎' : '🤖';
  };

  return (
    <div className="p-6">
      {/* Header with filters */}
      <div className="mb-4 flex items-center justify-between">
        <h4 className="font-medium text-gray-900">{t.tokens.title}</h4>
        <div className="flex items-center gap-2">
          <Select
            value={platformFilter}
            onChange={(e) => {
              setPlatformFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: '', label: t.tokens.allPlatforms },
              { value: 'ios', label: t.stats.ios },
              { value: 'android', label: t.stats.android },
            ]}
            className="w-36"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            {isLoading ? t.tokens.loading : t.tokens.refresh}
          </Button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-red-600 text-sm text-center py-8">
          {tc.error}: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      ) : tokens.length === 0 ? (
        <div className="text-gray-500 text-sm text-center py-8">
          <div className="text-4xl mb-2">📭</div>
          {t.tokens.noTokens}
        </div>
      ) : (
        <>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {tokens.map((token) => (
              <div
                key={token.id}
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{getPlatformIcon(token.platform)}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {token.platform.toUpperCase()}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs rounded-full ${
                          token.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {token.is_active ? t.tokens.active : t.tokens.inactive}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 truncate">
                      {t.tokens.user}: {token.user_id}
                    </p>
                    <p className="text-xs text-gray-500 font-mono truncate">
                      {token.push_token}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t.tokens.registered}: {formatDate(token.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {t.tokens.page} {page} {t.tokens.of} {totalPages} ({total} {t.tokens.total})
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  {t.tokens.previous}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  {t.tokens.next}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
